import { mdiChevronRight } from "@mdi/js";
import { useEffect, useId, useState } from "react";
import { api, type DueModule, type TrainingList } from "./api.ts";
import { calendarDayDifference, formatDate } from "./date.ts";
import { Icon } from "./Icon.tsx";
import { messages } from "./i18n.ts";
import { PageHeader } from "./PageHeader.tsx";

type LearnerModule = Omit<DueModule, "dueAt"> & {
  dueAt: string | null;
  completedAt?: string | null;
};
type LearnerTraining = Omit<TrainingList, "modules"> & {
  modules: LearnerModule[];
  history: LearnerModule[];
  optionalModules: LearnerModule[];
};
type LibraryModule = {
  moduleId: string;
  moduleKey: string;
  title: string;
  documentKeys: string;
  revision: string;
  obligationId: string | null;
};

function statusPill(module: LearnerModule, timezone: string, now: Date) {
  if (module.state === "complete")
    return {
      text: messages.common.complete,
      className: "bg-status-complete-bg text-status-complete-fg",
    };
  if (module.state === "superseded")
    return { text: messages.home.superseded, className: "bg-muted text-muted-foreground" };
  if (module.state === "cancelled")
    return { text: messages.home.cancelled, className: "bg-muted text-muted-foreground" };
  if (!module.dueAt)
    return { text: messages.home.optional, className: "bg-muted text-muted-foreground" };
  const days = calendarDayDifference(module.dueAt, now, timezone);
  if (days < 0)
    return {
      text: messages.home.overdueDays(Math.abs(days)),
      className: "bg-status-overdue-bg text-status-overdue-fg",
    };
  const text = days === 0 ? messages.home.dueToday : messages.home.dueIn(days);
  return module.acknowledgedCount > 0
    ? { text, className: "bg-status-in-progress-bg text-status-in-progress-fg" }
    : { text, className: "bg-status-not-started-bg text-status-not-started-fg" };
}

function progressText(module: LearnerModule, timezone: string): string {
  if (module.state === "complete")
    return module.completedAt
      ? messages.home.completedOn(formatDate(module.completedAt, timezone))
      : messages.home.completedWithApplicableQuiz;
  if (module.state === "superseded") return messages.home.superseded;
  if (module.state === "cancelled") return messages.home.cancelled;
  if (module.acknowledgedCount === 0 && !module.passed) return messages.common.notStarted;
  const read = messages.home.documentsRead(module.acknowledgedCount, module.documentCount);
  if (module.passed) return `${read} · ${messages.home.quizPassed}`;
  if (module.acknowledgedCount === module.documentCount) return messages.home.quizNotStarted;
  return read;
}

function ModuleRow({
  module,
  timezone,
  now,
  onOpen,
}: {
  module: LearnerModule;
  timezone: string;
  now: Date;
  onOpen: (id: string) => void;
}) {
  const titleId = useId();
  const pill = statusPill(module, timezone, now);
  return (
    <li className="relative border-b border-border last:border-b-0">
      <button
        aria-labelledby={titleId}
        className="absolute inset-0 z-10 w-full rounded-none focus-visible:outline-offset-[-2px]"
        type="button"
        onClick={() => onOpen(module.obligationId)}
      />
      <div className="grid min-h-11 grid-cols-[1fr_auto] items-center gap-x-3.5 gap-y-1 px-4 py-3 sm:grid-cols-[88px_minmax(0,1fr)_190px_150px_24px] sm:py-3.5">
        <span className="order-1 font-mono text-[12px] font-semibold text-muted-foreground sm:order-none">
          {module.documentKeys}
        </span>
        <div className="order-3 col-span-2 min-w-0 sm:order-none sm:col-span-1">
          <h3 className="text-[13px] font-bold text-foreground" id={titleId}>
            {module.title}
          </h3>
          {module.description ? (
            <p className="mt-px truncate text-[12px] text-muted-foreground">{module.description}</p>
          ) : null}
        </div>
        <p className="order-4 col-span-2 text-[12px] text-muted-foreground sm:order-none sm:col-span-1">
          {progressText(module, timezone)}
        </p>
        <span
          className={`pill order-2 justify-self-end sm:order-none sm:justify-self-start ${pill.className}`}
        >
          {pill.text}
        </span>
        <Icon
          className="order-5 hidden text-muted-foreground sm:block"
          path={mdiChevronRight}
          size={20}
        />
      </div>
    </li>
  );
}

function Group({
  label,
  modules,
  timezone,
  now,
  onOpen,
}: {
  label: string;
  modules: LearnerModule[];
  timezone: string;
  now: Date;
  onOpen: (id: string) => void;
}) {
  if (!modules.length) return null;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </h2>
      <ul className="overflow-hidden rounded-lg border border-border bg-card">
        {modules.map((module) => (
          <ModuleRow
            key={module.obligationId}
            module={module}
            now={now}
            onOpen={onOpen}
            timezone={timezone}
          />
        ))}
      </ul>
    </section>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: "overdue" }) {
  return (
    <div className="w-[150px] shrink-0 rounded-lg border border-border px-3 py-2.5">
      <p
        className={`text-xl font-bold ${tone === "overdue" && value > 0 ? "text-destructive" : "text-foreground"}`}
      >
        {value}
      </p>
      <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

export function Home({
  user,
  timezone,
  onOpen,
}: {
  user: { name: string; orgName: string };
  timezone: string;
  onOpen: (obligationId: string) => void;
}) {
  const [training, setTraining] = useState<LearnerTraining | null>(null);
  const [view, setView] = useState<"current" | "history" | "optional">("current");
  const [error, setError] = useState<string | null>(null);
  const [library, setLibrary] = useState<LibraryModule[]>([]);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [unsubscribing, setUnsubscribing] = useState<string | null>(null);
  const now = new Date();
  useEffect(() => {
    api
      .training()
      .then((value) => setTraining(value as unknown as LearnerTraining))
      .catch(() => setError(messages.home.loadError));
  }, []);
  useEffect(() => {
    if (view === "optional")
      api
        .trainingLibrary()
        .then((value) => setLibrary(value.modules))
        .catch(() => setError(messages.home.libraryLoadError));
  }, [view]);
  const current = training?.modules ?? [];
  const history = training?.history ?? [];
  const optional = training?.optionalModules ?? [];
  const outstanding = current.filter((module) => module.state !== "complete");
  const overdue = outstanding.filter(
    (module) => module.dueAt !== null && calendarDayDifference(module.dueAt, now, timezone) < 0,
  ).length;
  const visible = view === "current" ? current : view === "history" ? history : optional;
  const assign = async (moduleId: string) => {
    setAssigning(moduleId);
    try {
      await api.selfAssign(moduleId);
      const refreshed = await api.training();
      setTraining(refreshed as unknown as LearnerTraining);
      setLibrary((items) => items.filter((item) => item.moduleId !== moduleId));
    } catch {
      setError(messages.home.assignError);
    } finally {
      setAssigning(null);
    }
  };
  const unsubscribe = async (obligationId: string) => {
    setUnsubscribing(obligationId);
    try {
      await api.unsubscribe(obligationId);
      const refreshed = await api.training();
      setTraining(refreshed as unknown as LearnerTraining);
    } catch {
      setError(messages.home.unsubscribeError);
    } finally {
      setUnsubscribing(null);
    }
  };
  return (
    <>
      <PageHeader subtitle={user.orgName} title={messages.home.title}>
        {training ? (
          <section
            aria-label={messages.home.summary}
            className="mt-3.5 flex gap-2.5 overflow-x-auto"
          >
            <Tile label={messages.common.overdue} tone="overdue" value={overdue} />
            <Tile label={messages.home.assigned} value={training.summary.total} />
            <Tile label={messages.common.complete} value={training.summary.complete} />
          </section>
        ) : null}
      </PageHeader>
      <main className="flex flex-col gap-4 p-4 sm:p-7">
        {error ? <p className="text-destructive">{error}</p> : null}
        {training ? (
          current.length === 0 && history.length === 0 && optional.length === 0 ? (
            <p className="rounded-lg border border-border bg-card p-6 text-muted-foreground">
              {messages.home.nothingAssigned}
            </p>
          ) : (
            <>
              <nav
                aria-label={messages.home.views}
                className="flex gap-1 overflow-x-auto border-b border-border"
              >
                {(
                  [
                    ["current", messages.home.current],
                    ["history", messages.home.history],
                    ["optional", messages.home.optionalLibrary],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    aria-current={view === key ? "page" : undefined}
                    className={`border-b-2 px-3 py-2 text-sm font-medium ${view === key ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
                    key={key}
                    type="button"
                    onClick={() => setView(key)}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              {view === "current" ? (
                <>
                  {outstanding.length === 0 ? (
                    <p className="rounded-lg border border-border bg-card px-4 py-3 text-[13px] text-muted-foreground">
                      {messages.home.nothingOutstanding}
                    </p>
                  ) : null}
                  <Group
                    label={messages.home.doTheseFirst}
                    modules={outstanding}
                    now={now}
                    onOpen={onOpen}
                    timezone={timezone}
                  />
                  <Group
                    label={messages.home.completeGroup}
                    modules={current.filter((module) => module.state === "complete")}
                    now={now}
                    onOpen={onOpen}
                    timezone={timezone}
                  />
                </>
              ) : view === "optional" ? (
                <section className="flex flex-col gap-2">
                  <h2 className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    {messages.home.optionalLibrary}
                  </h2>
                  {optional.length ? (
                    <section className="flex flex-col gap-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        {messages.home.assignedOptional}
                      </h3>
                      <ul className="overflow-hidden rounded-lg border border-border bg-card">
                        {optional.map((module) => (
                          <li
                            className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
                            key={module.obligationId}
                          >
                            <button
                              className="min-w-0 flex-1 text-left"
                              type="button"
                              onClick={() => onOpen(module.obligationId)}
                            >
                              <span className="font-mono text-xs text-muted-foreground">
                                {module.documentKeys}
                              </span>
                              <span className="block font-semibold text-foreground">
                                {module.title}
                              </span>
                            </button>
                            <button
                              className="btn shrink-0"
                              disabled={unsubscribing === module.obligationId}
                              type="button"
                              onClick={() => void unsubscribe(module.obligationId)}
                            >
                              {unsubscribing === module.obligationId
                                ? messages.home.unsubscribing
                                : messages.home.unsubscribe}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                  <ul className="overflow-hidden rounded-lg border border-border bg-card">
                    {library.map((item) => (
                      <li
                        className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
                        key={item.moduleId}
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-muted-foreground">
                            {item.documentKeys}
                          </p>
                          <h3 className="font-semibold text-foreground">{item.title}</h3>
                          <p className="text-xs text-muted-foreground">
                            {messages.home.revision(item.revision)}
                          </p>
                        </div>
                        {item.obligationId ? (
                          <span className="pill bg-status-in-progress-bg text-status-in-progress-fg">
                            {messages.home.assignedOptional}
                          </span>
                        ) : (
                          <button
                            className="btn shrink-0"
                            disabled={assigning === item.moduleId}
                            type="button"
                            onClick={() => void assign(item.moduleId)}
                          >
                            {assigning === item.moduleId
                              ? messages.home.assigning
                              : messages.home.selfAssign}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {!optional.length && !library.length ? (
                    <p className="rounded-lg border border-border bg-card p-6 text-muted-foreground">
                      {messages.home.noOptional}
                    </p>
                  ) : null}
                </section>
              ) : visible.length ? (
                <Group
                  label={view === "history" ? messages.home.history : messages.home.optionalLibrary}
                  modules={visible}
                  now={now}
                  onOpen={onOpen}
                  timezone={timezone}
                />
              ) : (
                <p className="rounded-lg border border-border bg-card p-6 text-muted-foreground">
                  {view === "history" ? messages.home.noHistory : messages.home.noOptional}
                </p>
              )}
            </>
          )
        ) : !error ? (
          <p className="text-muted-foreground">{messages.common.loading}</p>
        ) : null}
      </main>
    </>
  );
}
